// Tâches : vues « Mes tâches » et « Toutes les tâches » (admin/media)
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TaskDialog } from "@/components/TaskDialog";
import { UserAvatar } from "@/components/UserAvatar";
import { PRIORITES, STATUTS_TACHE } from "@/lib/constants";
import { formatCourt, estEnRetard } from "@/lib/dates";

export default function Tasks() {
  const { user } = useAuth();
  const canEdit = ["admin", "media"].includes(user.role);
  const [scope, setScope] = useState("me");
  const [statut, setStatut] = useState("");
  const [taches, setTaches] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(null); // { tache } ou {}

  const charger = useCallback(async () => {
    try {
      const params = { scope };
      if (statut) params.statut = statut;
      const { data } = await api.get("/tasks", { params });
      setTaches(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [scope, statut]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const basculer = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { statut: t.statut === "termine" ? "a_faire" : "termine" });
      charger();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-bold">Tâches</h1>
          <p className="text-muted-foreground text-sm mt-1">Suivi des tâches de l'équipe média</p>
        </div>
        {canEdit && (
          <Button onClick={() => setDialog({})} className="rounded-full gap-1.5" data-testid="new-task-button">
            <Plus className="h-4 w-4" /> Nouvelle tâche
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList className="rounded-full">
            <TabsTrigger value="me" className="rounded-full" data-testid="tasks-tab-me">Mes tâches</TabsTrigger>
            {canEdit && <TabsTrigger value="all" className="rounded-full" data-testid="tasks-tab-all">Toutes les tâches</TabsTrigger>}
          </TabsList>
        </Tabs>
        <Select value={statut || "tous"} onValueChange={(v) => setStatut(v === "tous" ? "" : v)}>
          <SelectTrigger className="rounded-full h-9 w-auto min-w-[130px] text-sm" data-testid="tasks-filter-statut">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tous">Tous les statuts</SelectItem>
            {Object.entries(STATUTS_TACHE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2.5">
        {taches.map((t) => (
          <div key={t.id} className="flex items-center gap-3.5 bg-card rounded-2xl border border-border px-5 py-3.5 carte-douce" data-testid={`task-row-${t.id}`}>
            <Checkbox checked={t.statut === "termine"} onCheckedChange={() => basculer(t)} data-testid={`task-toggle-${t.id}`} />
            <button onClick={() => canEdit && setDialog({ tache: t })} className={`flex-1 min-w-0 text-left ${canEdit ? "cursor-pointer" : "cursor-default"}`}>
              <p className={`text-sm font-medium truncate ${t.statut === "termine" ? "line-through text-muted-foreground" : ""}`}>
                {t.titre}
                {t.lien_type && <Link2 className="inline h-3.5 w-3.5 ml-1.5 text-muted-foreground" />}
              </p>
              {t.description && <p className="text-xs text-muted-foreground truncate">{t.description}</p>}
            </button>
            <Badge className={`${PRIORITES[t.priorite]?.classe} border-0 hidden sm:inline-flex`}>{PRIORITES[t.priorite]?.label}</Badge>
            <Badge variant="outline" className="hidden sm:inline-flex">{STATUTS_TACHE[t.statut]?.label}</Badge>
            {t.echeance && (
              <span className={`text-xs shrink-0 ${estEnRetard(t.echeance) && t.statut !== "termine" ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                {formatCourt(t.echeance)}
              </span>
            )}
            <div className="flex -space-x-2 shrink-0">
              {t.assignes.map((id) => {
                const u = users.find((x) => x.id === id);
                return u ? <UserAvatar key={id} user={u} size="sm" /> : null;
              })}
            </div>
          </div>
        ))}
        {taches.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-12">Aucune tâche à afficher.</p>
        )}
      </div>

      <TaskDialog
        open={Boolean(dialog)}
        onClose={() => setDialog(null)}
        tache={dialog?.tache}
        users={users}
        onSaved={charger}
      />
    </div>
  );
}
